import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertCircle, Loader, AlertTriangle, Info, CircleX, Filter, MonitorDot, MonitorCog, ExternalLink, Database, Square } from "lucide-react";
import { Job } from '../lib/types';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime, titleCaseSnake } from '../lib/text';
import { JobStatusIcon, jobStatusBgColor } from '../lib/jobs';
import { PageLoader } from '../components/PageLoader';
import { PageError } from '../components/PageError';

type MessageLevel = 'all' | 'info' | 'warning' | 'error';

const JobDetailPage = () => {
    const { authFetch } = useAuth();
    const location = useLocation();
    const [job, setJob] = useState<Job | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [jobMessages, setJobMessages] = useState<Array<{ id: string; message: string; level: string; created_at: string }>>([]);
    const [messageFilter, setMessageFilter] = useState<MessageLevel>('all');
    const [killing, setKilling] = useState(false);

    const isInFlight = job ? ['pending', 'queued', 'processing'].includes(job.status) : false;

    const handleKill = async () => {
        if (!job_id) return;
        if (!window.confirm('Stop this job? Any running task will be terminated and the job marked failed.')) return;
        setKilling(true);
        try {
            const res = await authFetch(`/jobs/${job_id}/kill`, { method: 'POST' });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || 'Failed to stop job');
            }
            setJob(await res.json());
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to stop job');
        } finally {
            setKilling(false);
        }
    };

    const pathParts = location.pathname.split('/').filter(p => p);
    const job_id = pathParts[1];

    useEffect(() => {
        if (!job_id) return;

        // Chain each refresh off the previous response so one fetch is in flight at a time.
        let cancelled = false;
        let timer: number | null = null;
        const tick = async () => {
            try {
                const [jobRes, messagesRes] = await Promise.all([
                    authFetch(`/jobs/${job_id}`),
                    authFetch(`/jobs/${job_id}/messages`)
                ]);
                if (cancelled) return;

                if (!jobRes.ok) throw new Error('Failed to load job details');
                if (!messagesRes.ok) throw new Error('Failed to load job messages');

                const jobData = await jobRes.json();
                const messagesData = await messagesRes.json();

                setJob(jobData as Job);
                setJobMessages(messagesData);
                setLoading(false);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : 'Failed to load job details');
                setLoading(false);
            } finally {
                if (!cancelled) timer = window.setTimeout(tick, 3000);
            }
        };

        tick();

        return () => {
            cancelled = true;
            if (timer !== null) clearTimeout(timer);
        };
    }, [job_id]);


    const getLevelColor = (level: string) => {
        switch (level) {
            case 'error':
                return 'border-state-error';
            case 'warning':
                return 'border-state-warning';
            case 'info':
            default:
                return 'border-brand';
        }
    };

    const getLevelBadge = (level: string) => {
        switch (level) {
            case 'error':
                return <CircleX className="w-6 h-6 text-state-error" />;
            case 'warning':
                return <AlertTriangle className="w-6 h-6 text-state-warning" />;
            case 'info':
            default:
                return <Info className="w-6 h-6 text-brand" />;
        }
    };

    const getFilteredMessages = () => {
        if (messageFilter === 'all') {
            return jobMessages;
        }
        return jobMessages.filter(msg => msg.level === messageFilter);
    };

    const getMessageCountByLevel = (level: string) => {
        return jobMessages.filter(msg => msg.level === level).length;
    };

    if (loading) return <PageLoader label="Loading job details..." />;
    if (error || !job) return <PageError title="Failed to load job" message={error || 'Job not found'} />;

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl border border-softgrey flex items-center justify-center shrink-0 ${jobStatusBgColor(job.status)}`}>
                        <JobStatusIcon status={job.status} />
                    </div>
                    {isInFlight && (
                        <button
                            onClick={handleKill}
                            disabled={killing}
                            className="order-last flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-state-error bg-state-error/10 hover:bg-state-error/20 border border-state-error/30 rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shrink-0 self-center"
                        >
                            {killing ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
                            Kill Job
                        </button>
                    )}
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-graphite">
                            {job.task_type ? titleCaseSnake(job.task_type) : job.id}
                        </h3>
                        <div className="flex flex-wrap items-center gap-5 text-sm text-coolslate mt-1">
                            {job.source_type && job.source && (
                                <Link to={`/sources/${job.source_id}`} className="inline-flex items-center gap-2 group">
                                    <Database className="w-4 h-4 text-brand" />
                                    <span className="group-hover:text-brand transition-colors">{job.source}</span>
                                    <ExternalLink className="w-3.5 h-3.5 text-coolslate/70 group-hover:text-brand transition-colors" />
                                </Link>
                            )}
                            {job.created_at && (
                                <span className="inline-flex items-center gap-1.5"><MonitorDot className="w-4 h-4 text-brand" />{formatDateTime(job.created_at)}</span>
                            )}
                            {job.updated_at && (
                                <span className="inline-flex items-center gap-1.5"><MonitorCog className="w-4 h-4 text-brand" />{formatDateTime(job.updated_at)}</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="space-y-6">
                    {job.error && job.status === 'failed' && (
                        <div className="bg-state-error/10 border border-state-error/30 rounded-lg p-4">
                            <h4 className="font-semibold text-graphite mb-2 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                Error Details
                            </h4>
                            <p className="text-sm text-state-error">{job.error}</p>
                        </div>
                    )}

                    {jobMessages.length > 0 && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-graphite flex items-center gap-2">
                                    Messages
                                </h4>
                                <div className="flex items-center gap-2">
                                    <Filter className="w-4 h-4" />
                                    <button
                                        onClick={() => setMessageFilter('all')}
                                        className={`px-3 py-1 text-xs rounded-full transition-colors ${messageFilter === 'all'
                                            ? 'bg-graphite text-white'
                                            : 'bg-softgrey text-graphite hover:bg-softgrey/80'
                                            }`}
                                    >
                                        All ({jobMessages.length})
                                    </button>
                                    <button
                                        onClick={() => setMessageFilter('info')}
                                        className={`px-3 py-1 text-xs rounded-full transition-colors ${messageFilter === 'info'
                                            ? 'bg-brand text-white'
                                            : 'bg-brand/15 text-brand hover:bg-brand/25'
                                            }`}
                                    >
                                        Info ({getMessageCountByLevel('info')})
                                    </button>
                                    <button
                                        onClick={() => setMessageFilter('warning')}
                                        className={`px-3 py-1 text-xs rounded-full transition-colors ${messageFilter === 'warning'
                                            ? 'bg-state-warning text-white'
                                            : 'bg-state-warning/15 text-state-warning hover:bg-state-warning/25'
                                            }`}
                                    >
                                        Warning ({getMessageCountByLevel('warning')})
                                    </button>
                                    <button
                                        onClick={() => setMessageFilter('error')}
                                        className={`px-3 py-1 text-xs rounded-full transition-colors ${messageFilter === 'error'
                                            ? 'bg-state-error text-white'
                                            : 'bg-state-error/15 text-state-error hover:bg-state-error/25'
                                            }`}
                                    >
                                        Error ({getMessageCountByLevel('error')})
                                    </button>
                                </div>
                            </div>
                            {getFilteredMessages().map((msg) => (
                                <div key={msg.id} className={`border-l-2 ${getLevelColor(msg.level)} pl-3 py-1`}>
                                    <div className="flex items-start gap-2">
                                        <div className="my-auto">
                                            {getLevelBadge(msg.level)}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm text-graphite">{msg.message}</p>
                                            <p className="text-xs text-coolslate mt-0.5">
                                                {formatDateTime(msg.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                {jobMessages.length === 0 && job.status !== 'success' && job.status !== 'failed' && (
                    <div className="text-center py-8 text-coolslate">
                        <Loader className="w-8 h-8 animate-spin mx-auto mb-2" />
                        <p>Waiting for processing updates...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobDetailPage;
